import React from "react";
import {
  Box,
  Typography,
  TextField,
  Select,
  MenuItem,
} from "@mui/material";
import { FormattedMessage, useIntl } from "react-intl";
import IconCardSelect from "./IconCardSelect";
import {
  IndependentIcon,
  EmployeeIcon,
  WorkmanIcon,
  StudentIcon,
} from "./onboardingIcons";
import { candidateStatus, degrees, frenchDegrees } from "bundles/common/utils/constants";

const statusIcons = {
  independent: IndependentIcon,
  employee: EmployeeIcon,
  workman: WorkmanIcon,
  student: StudentIcon,
};

const inputSx = {
  "& .MuiOutlinedInput-root": {
    fontFamily: "'Segoe UI', system-ui, sans-serif",
    fontSize: "1rem",
    borderRadius: "0.5rem",
    backgroundColor: "hsl(0, 0%, 100%)",
    "& fieldset": { borderColor: "hsl(220, 13%, 71%)" },
    "&:hover fieldset": { borderColor: "hsl(220, 13%, 50%)" },
    "&.Mui-focused fieldset": { borderColor: "hsl(153, 68%, 25%)" },
  },
  "& .MuiInputLabel-root": {
    fontFamily: "'Segoe UI', system-ui, sans-serif",
    color: "hsl(215.4, 16.3%, 46.9%)",
    "&.Mui-focused": { color: "hsl(153, 68%, 25%)" },
    "& .MuiFormLabel-asterisk": { color: "hsl(0, 84.2%, 60.2%)" },
  },
  "& .MuiInputBase-input": {
    color: "hsl(222.2, 84%, 4.9%)",
  },
};

const selectSx = {
  fontFamily: "'Segoe UI', system-ui, sans-serif",
  fontSize: "1rem",
  borderRadius: "0.5rem",
  backgroundColor: "hsl(0, 0%, 100%)",
  "& .MuiOutlinedInput-notchedOutline": {
    borderColor: "hsl(220, 13%, 71%)",
  },
  "&:hover .MuiOutlinedInput-notchedOutline": {
    borderColor: "hsl(220, 13%, 50%)",
  },
  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
    borderColor: "hsl(153, 68%, 25%)",
  },
};

const labelSx = {
  fontFamily: "'Segoe UI Semibold', system-ui, sans-serif",
  fontSize: "1rem",
  color: "hsl(222.2, 84%, 4.9%)",
  mb: "0.75rem",
  "& sup": {
    color: "hsl(0, 84.2%, 60.2%)",
    marginLeft: "0.15rem",
    fontSize: "0.75em",
    lineHeight: 0,
  },
};

const StepAboutYou = ({ values, errors, handleCardSelected, handleInputValue }) => {
  const intl = useIntl();

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: "1.5rem" }}>
      {/* Status */}
      <Box id="ob-field-candidate_status">
        <Typography sx={labelSx}>
          <FormattedMessage id="onboarding.status" />
          <sup>*</sup>
        </Typography>
        <IconCardSelect
          fieldName="candidate_status"
          selected={values.candidate_status}
          values={candidateStatus.map((status) => ({
            code: status,
            icon: statusIcons[status],
            label: intl.formatMessage({ id: `candidate.status_map.${status}` }),
          }))}
          onChange={handleCardSelected}
        />
        {errors.candidate_status && (
          <Typography sx={{ color: "hsl(0, 84.2%, 60.2%)", fontSize: "0.75rem", mt: "0.25rem" }}>
            {errors.candidate_status}
          </Typography>
        )}
      </Box>

      {/* Experience */}
      <Box id="ob-field-experience_year">
        <Typography sx={labelSx}>
          <FormattedMessage id="onboarding.experience" />
          <sup>*</sup>
        </Typography>
        <TextField
          fullWidth
          required
          value={values.experience_year}
          type="number"
          InputProps={{ inputProps: { min: 0, max: 100, inputMode: "numeric" } }}
          name="experience_year"
          onBlur={handleInputValue}
          onChange={handleInputValue}
          variant="outlined"
          sx={inputSx}
          label={<FormattedMessage id="candidate.experience" />}
          {...(errors.experience_year && {
            error: true,
            helperText: errors.experience_year,
          })}
        />
      </Box>

      {/* Job title */}
      <Box id="ob-field-job_title">
        <Typography sx={labelSx}>
          <FormattedMessage id="onboarding.job_title" />
          <sup>*</sup>
        </Typography>
        <TextField
          fullWidth
          required
          value={values.job_title}
          type="text"
          name="job_title"
          onBlur={handleInputValue}
          onChange={handleInputValue}
          variant="outlined"
          sx={inputSx}
          label={<FormattedMessage id="candidate.job_title" />}
          {...(errors.job_title && {
            error: true,
            helperText: errors.job_title,
          })}
        />
      </Box>

      {/* Degree */}
      <Box id="ob-field-highest_degree">
        <Typography sx={labelSx}>
          <FormattedMessage id="onboarding.degree_question" />
          <sup>*</sup>
        </Typography>
        <Select
          fullWidth
          name="highest_degree"
          variant="outlined"
          value={values.highest_degree || ""}
          onChange={handleInputValue}
          displayEmpty
          sx={selectSx}
          renderValue={(selected) =>
            selected ? (
              <FormattedMessage id={`onboarding.degree.${selected}`} />
            ) : (
              <Box component="span" sx={{ color: "hsl(215.4, 16.3%, 46.9%)" }}>
                <FormattedMessage id="onboarding.degree.degree" />
              </Box>
            )
          }
        >
          {[...degrees, ...frenchDegrees].map((degree) => (
            <MenuItem key={degree.code} value={degree.code}>
              <FormattedMessage id={degree.label} />
            </MenuItem>
          ))}
        </Select>
        {errors.highest_degree && (
          <Typography sx={{ color: "hsl(0, 84.2%, 60.2%)", fontSize: "0.75rem", mt: "0.25rem" }}>
            {errors.highest_degree}
          </Typography>
        )}
      </Box>
    </Box>
  );
};

export default StepAboutYou;
